import {
  AlertCircle,
  FolderOpen,
  Loader2,
  Save,
  Upload,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  parseDocumentPublishConfigFile,
  getDocumentPublishConfigPath,
  getDocumentPublishDir,
  normalizeSingleDocPublishOutDir,
  resolveSingleDocPublishOutDir,
} from "../../lib/publish-utils";
import { getPlatformService } from "../../platform";
import { useEditorStore } from "../../store/editor-store";
import {
  isActiveRunningPublishTask,
  usePublishStore,
} from "../../store/publish-store";
import { useThemeStore } from "../../theme/runtime/theme-store";
import { useShallow } from "zustand/shallow";
import { showFolderPickerDialog } from "./DialogHost";
import { DialogButton, DialogOverlay } from "./DialogOverlay";
import { PublishTargetSummary } from "./PublishTargetSummary";

interface DocumentPublishDialogRequest {
  filePath: string;
  resolve: () => void;
}

interface DocumentPublishDialogValues {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  useOpenBufferContent: boolean;
}

let _listener: ((req: DocumentPublishDialogRequest) => void) | null = null;

export function showDocumentPublishDialog(filePath: string): Promise<void> {
  return new Promise((resolve) => {
    if (_listener) {
      _listener({ filePath, resolve });
    } else {
      console.warn("[document-publish-dialog] No host mounted");
      resolve();
    }
  });
}

export function DocumentPublishDialogHost(): ReactNode {
  const [request, setRequest] = useState<DocumentPublishDialogRequest | null>(
    null,
  );

  useEffect(() => {
    _listener = (req) => setRequest(req);
    return () => {
      _listener = null;
    };
  }, []);

  if (!request) return null;
  return (
    <DocumentPublishDialog
      filePath={request.filePath}
      onClose={() => {
        request.resolve();
        setRequest(null);
      }}
    />
  );
}

function buildDefaultValues(
  filePath: string,
  useOpenBufferContent: boolean,
): DocumentPublishDialogValues {
  return {
    outDir: resolveSingleDocPublishOutDir(filePath) ?? "",
    clean: false,
    rerunAll: false,
    useOpenBufferContent,
  };
}

function DocumentPublishDialog({
  filePath,
  onClose,
}: {
  filePath: string;
  onClose: () => void;
}) {
  const t = useT("editor");
  const setStatusMessage = useEditorStore((s) => s.setStatusMessage);
  const fileTasks = usePublishStore(useShallow((s) => s.fileTasks));
  const currentTheme = useThemeStore(
    useShallow((s) => ({
      themeId: s.resolvedThemeId,
      contentProfileId: s.contentProfile,
      presentationProfileId: s.presentationProfile,
    })),
  );

  // Use useMemo to avoid re-creating these values on every render
  // and breaking the Zustand subscription
  const { hasOpenBuffer, hasUnsavedChanges } = useMemo(() => {
    const tabs = useEditorStore.getState().tabs;
    const foundTab = tabs.find((entry) => entry.filePath === filePath);
    return {
      hasOpenBuffer: !!foundTab,
      hasUnsavedChanges: !!foundTab?.isDirty,
    };
  }, [filePath]);

  const publishTask = fileTasks[filePath];

  const defaultOutDir = useMemo(
    () => resolveSingleDocPublishOutDir(filePath) ?? "",
    [filePath],
  );
  const publishPackagePath = useMemo(
    () => getDocumentPublishDir(filePath),
    [filePath],
  );

  const [values, setValues] = useState<DocumentPublishDialogValues>(() =>
    buildDefaultValues(filePath, hasOpenBuffer),
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWarning(null);

    const defaults = buildDefaultValues(filePath, hasOpenBuffer);
    const configPath = getDocumentPublishConfigPath(filePath);

    void (async () => {
      try {
        const engine = getPlatformService().engine;
        const { exists } = await engine.workspaceExists({ path: configPath });
        if (!exists) {
          if (!cancelled) {
            setValues(defaults);
          }
          return;
        }

        const { content } = await engine.workspaceReadFile({
          path: configPath,
        });
        const parsed = parseDocumentPublishConfigFile(content, filePath);
        if (!cancelled) {
          if (parsed) {
            setValues({
              outDir:
                normalizeSingleDocPublishOutDir(
                  filePath,
                  parsed.settings.outDir,
                ) ?? defaultOutDir,
              clean: parsed.settings.clean,
              rerunAll: parsed.settings.rerunAll,
              useOpenBufferContent: hasOpenBuffer
                ? parsed.settings.useOpenBufferContent
                : false,
            });
          } else {
            setValues(defaults);
            setWarning(t("publish.warning.invalidConfig"));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setValues(defaults);
          setWarning(
            t("publish.warning.readConfigFailed", {
              message: err instanceof Error ? err.message : String(err),
            }),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [defaultOutDir, filePath, hasOpenBuffer]);

  const isRunning = isActiveRunningPublishTask(publishTask);
  const resolvedOutDir =
    normalizeSingleDocPublishOutDir(filePath, values.outDir) ?? defaultOutDir;
  const isCustomOutDir =
    !!resolvedOutDir && !!defaultOutDir && resolvedOutDir !== defaultOutDir;
  const contentSourceLabel =
    values.useOpenBufferContent && hasOpenBuffer
      ? t("publish.contentSource.openBuffer")
      : t("publish.contentSource.diskFile");
  const themeSnapshotLabel = `${currentTheme.themeId} / ${currentTheme.contentProfileId} / ${currentTheme.presentationProfileId}`;

  const handleSave = async () => {
    if (!resolvedOutDir) {
      setError(t("publish.error.outDirRequired"));
      return;
    }
    setBusy("save");
    setError(null);
    try {
      const saveFn = usePublishStore.getState().saveDocumentPublishConfig;
      await saveFn(filePath, {
        outDir: resolvedOutDir,
        clean: values.clean,
        rerunAll: values.rerunAll,
        useOpenBufferContent: values.useOpenBufferContent,
      });
      setStatusMessage(t("publish.status.configSavedInPackage"));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = async () => {
    if (!resolvedOutDir) {
      setError(t("publish.error.outDirRequired"));
      return;
    }
    console.log(
      "[DocumentPublishDialog] Starting publish for",
      filePath,
      "outDir:",
      resolvedOutDir,
    );
    setBusy("publish");
    setError(null);
    try {
      const publishFileFn = usePublishStore.getState().publishFile;
      const started = await publishFileFn(filePath, {
        outDir: resolvedOutDir,
        clean: values.clean,
        rerunAll: values.rerunAll,
        useOpenBufferContent: values.useOpenBufferContent,
      });
      console.log("[DocumentPublishDialog] Publish started:", started);
      if (started) {
        onClose();
      } else {
        setError(t("publish.error.startFailed"));
      }
    } catch (err) {
      console.error("[DocumentPublishDialog] Publish error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DialogOverlay
      open
      onClose={busy ? () => {} : onClose}
      title={t("publish.document")}
      className="w-[680px]"
      footer={
        <>
          <DialogButton onClick={onClose} disabled={!!busy}>
            {t("common.cancel")}
          </DialogButton>
          <DialogButton
            onClick={() => {
              void handleSave();
            }}
            disabled={loading || !!busy}
          >
            <span className="inline-flex items-center gap-1">
              <Save size={12} />
              {t("publish.action.saveConfig")}
            </span>
          </DialogButton>
          <DialogButton
            variant="primary"
            onClick={() => {
              void handlePublish();
            }}
            disabled={loading || !!busy || isRunning}
          >
            <span className="inline-flex items-center gap-1">
              {busy === "publish" ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Upload size={12} />
              )}
              {t("publish.action.start")}
            </span>
          </DialogButton>
        </>
      }
    >
      <div className="flex h-full flex-col overflow-auto px-4 py-4">
        <div className="space-y-4">
          <PublishTargetSummary
            kind="document"
            targetPath={filePath}
            publishPackagePath={publishPackagePath}
            details={[
              {
                label: t("publish.field.outputDir"),
                value: resolvedOutDir || t("publish.notSet"),
                title: resolvedOutDir,
                mono: true,
              },
              {
                label: t("publish.field.contentSource"),
                value: (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span>{contentSourceLabel}</span>
                    {hasUnsavedChanges && values.useOpenBufferContent && (
                      <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1 py-0.5 text-[9px] text-amber-700">
                        {t("publish.unsavedChanges")}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                label: t("publish.field.themeSnapshot"),
                value: themeSnapshotLabel,
                title: themeSnapshotLabel,
                mono: true,
              },
            ]}
          />

          <section className="space-y-3">
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground">
                {t("publish.field.outputDir")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={values.outDir}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      outDir: event.target.value,
                    }))
                  }
                  disabled={loading || !!busy}
                  placeholder={defaultOutDir || publishPackagePath}
                  className="h-8 flex-1 rounded border border-border bg-background px-2 font-mono text-[11px]"
                />
                <button
                  type="button"
                  disabled={loading || !!busy}
                  onClick={() => {
                    void (async () => {
                      const chosen = await showFolderPickerDialog({
                        title: t("publish.chooseDocumentOutputDir"),
                      });
                      if (chosen) {
                        setValues((prev) => ({
                          ...prev,
                          outDir: chosen,
                        }));
                      }
                    })();
                  }}
                  className="inline-flex h-8 items-center justify-center rounded border border-border bg-muted px-2 text-muted-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
                  title={t("dialog.selectFolder")}
                >
                  <FolderOpen size={13} />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/70">
                {t("publish.defaultOutDirHelp", { dir: defaultOutDir || publishPackagePath })}
              </p>
              {isCustomOutDir && (
                <p className="text-[10px] text-amber-600">
                  {t("publish.customDocumentOutDirWarning")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={values.clean}
                  disabled={loading || !!busy}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      clean: event.target.checked,
                    }))
                  }
                />
                {t("publish.option.clean")}
              </label>
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={values.rerunAll}
                  disabled={loading || !!busy}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      rerunAll: event.target.checked,
                    }))
                  }
                />
                {t("publish.option.rerunAll")}
              </label>
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={values.useOpenBufferContent}
                  disabled={loading || !!busy || !hasOpenBuffer}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      useOpenBufferContent: event.target.checked,
                    }))
                  }
                />
                {hasUnsavedChanges
                  ? t("publish.option.useOpenBufferWithUnsaved")
                  : t("publish.option.useOpenBuffer")}
              </label>
              {!hasOpenBuffer && (
                <p className="text-[10px] text-muted-foreground/70">
                  {t("publish.noOpenBuffer")}
                </p>
              )}
            </div>
          </section>

          {(warning || error || isRunning) && (
            <section className="space-y-2">
              {isRunning && (
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-700">
                  {t("publish.documentRunningHint")}
                </div>
              )}
              {warning && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                  {warning}
                </div>
              )}
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                  <span className="inline-flex items-center gap-1">
                    <AlertCircle size={12} />
                    {error}
                  </span>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </DialogOverlay>
  );
}
